import type { Logger } from '@mimir/common/types/logger';
import { DecodedVideoFrame } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import type { VideoDecoder as VideoDecoderInterface, VideoPacket, VideoCodec } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import { approximateFractionValue } from '@mimir/common/util/integerMath';
import { exceptionToString } from '@mimir/common/util/exception';
import { Guard } from '@mimir/common/async/async';
import { calculateRawVideoFrameBufferSize } from '@mimir/common/transcoding/rawFrame';
import { PixelFormat } from '@mimir/common/transcoding/types';
import { abortify } from '@mimir/common/util/abortify';
import { makeOpaqueRefCountedBuffer } from '@mimir/browser/buffer/videoFrameBuffer';
import { OpaqueRefCountedBuffer } from '@mimir/cutter/frameProvider/frameProvider';
import { ImageBitmapBuffer } from '@mimir/browser/gldraw/imageBitmapBuffer';

export interface WebVideoDecoderParameters {
  logger: Logger,
  codec: VideoCodec,
  copyVideoFrameToImageBitmap: boolean,
}

export function createWebVideoDecoder(parameters: WebVideoDecoderParameters): VideoDecoderInterface {
  return createRecreatingVideoDecoder(parameters);
}

/// -----


const MAX_DECODE_QUEUE_SIZE : number = 5;

function videoCodecToCodecString(videoCodec: VideoCodec): string {
  switch (videoCodec) {
    case 'h264': return 'avc1.42c020';
    default:
      throw new Error(`Unsupported video codec: ${videoCodec}`);
  }
}

function webApiVideoPixelFormatToPixelFormat(vpf: VideoPixelFormat | null): PixelFormat {
  switch (vpf) {
    case 'I420': return 'yuv420p';
    case 'RGBA': return 'rgba';
    case 'BGRX': return 'bgr0';
    case 'NV12': return 'nv12';
    default:
      throw new Error(`Unsupported pixel format: ${vpf}`);
  }
}

/**
 *  It seems that it is not safe to reuse the same VideoDecoder instance by
 *  using reset. Sometimes the reset does not take effect and you end up
 *  getting the last packet from the previous run and nothing else. Because
 *  of this we wrap the inner decoder in this wrapper decoder that recreates
 *  it on reset(). If the browsers ever get their shit together we can remove
 *  this workaround.
 */
function createRecreatingVideoDecoder(parameters: WebVideoDecoderParameters): VideoDecoderInterface {
  let closed = false;
  let inner : VideoDecoderInterface | null = null;

  const ensureInner = (): VideoDecoderInterface => {
    if (!inner) {
      inner = createWebVideoDecoderInternal(parameters);
    }
    return inner;
  };

  return {
    close(): void {
      this.reset();
      closed = true;
    },
    async push(packet: VideoPacket, abortSignal: AbortSignal): Promise<void> {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      await ensureInner().push(packet, abortSignal);
    },
    async poll(abortSignal: AbortSignal): Promise<DecodedVideoFrame | null> {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      return await ensureInner().poll(abortSignal);
    },
    async flush(abortSignal: AbortSignal): Promise<void> {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      await ensureInner().flush(abortSignal);
    },
    reset(): void {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      if (inner !== null) {
        inner.close();
        inner = null;
      }
    },
  };
}


function createWebVideoDecoderInternal(parameters: WebVideoDecoderParameters): VideoDecoderInterface {
  const { logger, codec, copyVideoFrameToImageBitmap } = parameters;

  const dequeue = new Guard();

  const pendingVideoFrames : VideoFrame[] = [];

  const videoDecoder =
    new VideoDecoder({
      error: (error) => {
        logger.error(`WebVideoDecoder: error=${exceptionToString(error)} (codec=${codec})`);
      },
      output: (videoFrame: VideoFrame) => {
        pendingVideoFrames.push(videoFrame);
      },
    });

  videoDecoder.ondequeue = () => { dequeue.notify(); };

  let isConfigured = false;

  const pendingKeyFrames = new Set();

  return {
    reset(): void {
      for (const videoFrame of pendingVideoFrames.splice(0)) {
        videoFrame.close();
      }
      videoDecoder.reset();
      pendingKeyFrames.clear();
      isConfigured = false;
    },
    close(): void {
      this.reset();
      videoDecoder.close();
    },

    async push(packet: VideoPacket, abortSignal: AbortSignal): Promise<void> {
      if (!isConfigured) {
        videoDecoder.configure({
          codec: videoCodecToCodecString(codec),
          description: packet.description,
          optimizeForLatency: true,
        });
        isConfigured = true;
      }

      while (videoDecoder.decodeQueueSize > MAX_DECODE_QUEUE_SIZE) {
        await abortify(abortSignal, () => dequeue.wait());
      }

      const timestampInMicroSeconds =
        Math.round(approximateFractionValue(packet.pts) * 1_000_000);

      if (packet.keyFrame) {
        pendingKeyFrames.add(timestampInMicroSeconds);
      }

      const chunk =
        new EncodedVideoChunk({
          data: packet.payload,
          timestamp: timestampInMicroSeconds,
          type: packet.keyFrame ? 'key' : 'delta',
        });

      videoDecoder.decode(chunk);
    },

    async poll(_abortSignal: AbortSignal): Promise<DecodedVideoFrame | null> {
      const [videoFrame] = pendingVideoFrames.splice(0, 1);
      if (videoFrame === undefined) {
        return null;
      }

      const timestamp = { numerator: videoFrame.timestamp, denominator: 1_000_000 };
      if (videoFrame.visibleRect === null) {
        throw new Error('You got rected');
      }

      const width = videoFrame.visibleRect.width;
      const height = videoFrame.visibleRect.height;

      const pixelFormat = webApiVideoPixelFormatToPixelFormat(videoFrame.format);
      const bufferSize = calculateRawVideoFrameBufferSize(width, height, pixelFormat);

      let keyFrame = pendingKeyFrames.delete(videoFrame.timestamp);

      let buffer : OpaqueRefCountedBuffer;
      if (copyVideoFrameToImageBitmap) {
        const bitmap = await createImageBitmap(videoFrame);
        videoFrame.close();
        buffer = new ImageBitmapBuffer(bitmap);
      } else {
        buffer = makeOpaqueRefCountedBuffer(bufferSize, videoFrame, { refCount: 0 });
      }


      return {
        pts: timestamp,
        keyFrame,
        info: {
          resolution: { width, height },
          fieldOrder: 'progressive',
          pixelFormat,
        },
        buffer,
      };
    },

    async flush(abortSignal: AbortSignal): Promise<void> {
      if (!isConfigured) {
        return;
      }
      await abortify(abortSignal, () => videoDecoder.flush());
      while (videoDecoder.decodeQueueSize > MAX_DECODE_QUEUE_SIZE) {
        await abortify(abortSignal, () => dequeue.wait());
      }
    }
  }
}
